export type ContentFeedPlatform =
  | "all"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "twitter"
  | "linkedin"
  | "podcast";

export type InstagramShowOption =
  | "all"
  | "both"
  | "post"
  | "reels"
  | "stories";
export type PodcastDisplayOption = "carousel" | "banner";

export type ContentFeedSelection = {
  kind: "carousel" | "latest-podcast";
  contentType: string;
  platform: string;
  title: string;
  activeFilter?: InstagramShowOption;
};

export const CONTENT_FEED_PLATFORMS: Array<{
  id: ContentFeedPlatform;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "twitter", label: "X" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "podcast", label: "Podcast" },
];

export const CUSTOM_CONTENT_PLATFORM_PILLS: Array<{ id: string; label: string }> = [
  { id: "All", label: "All" },
  ...CONTENT_FEED_PLATFORMS.filter((p) => p.id !== "all"),
];


export function getCustomContentPlatformPills(current?: string) {
  const value = current || "All";
  if (CUSTOM_CONTENT_PLATFORM_PILLS.some((option) => option.id === value)) {
    return CUSTOM_CONTENT_PLATFORM_PILLS;
  }
  const label =
    value === "MISC_AGG" || value === "MISC-content" || value === "MISC"
      ? "Added By Me"
      : value;
  return [...CUSTOM_CONTENT_PLATFORM_PILLS, { id: value, label }];
}

export const CUSTOM_PRODUCT_PLATFORM_PILLS: Array<{ id: string; label: string }> = [
  { id: "All", label: "All" },
  { id: "ShopMy", label: "ShopMy" },
  { id: "Amazon", label: "Amazon" },
  { id: "LTK", label: "LTK" },
  { id: "MISC", label: "Added By Me" },
];

export function getCustomProductPlatformPills(current?: string) {
  const value =
    current === "MISC_AGG" || current === "MISC-product" ? "MISC" : current || "All";
  // Never append content platforms (instagram, etc.) — Custom Products pills
  // stay product-only even if a stale filter value leaks in.
  if (CUSTOM_PRODUCT_PLATFORM_PILLS.some((option) => option.id === value)) {
    return CUSTOM_PRODUCT_PLATFORM_PILLS;
  }
  return CUSTOM_PRODUCT_PLATFORM_PILLS;
}

export function getCustomFeedPlatformPills(
  isShopLinks: boolean,
  current?: string
) {
  return isShopLinks
    ? getCustomProductPlatformPills(current)
    : getCustomContentPlatformPills(current);
}

export const INSTAGRAM_SHOW_OPTIONS: Array<{
  id: InstagramShowOption;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "both", label: "Posts & Reels" },
  { id: "post", label: "Posts" },
  { id: "reels", label: "Reels" },
  { id: "stories", label: "Stories" },
];

export const PODCAST_DISPLAY_OPTIONS: Array<{
  id: PodcastDisplayOption;
  label: string;
}> = [
  { id: "carousel", label: "Carousel" },
  { id: "banner", label: "Banner" },
];

const PLATFORM_PREFERENCE: ContentFeedPlatform[] = [
  "all",
  "instagram",
  "tiktok",
  "youtube",
  "twitter",
  "linkedin",
  "podcast",
];

/** Content platforms included in Recent Content "All" (excludes product marketplaces). */
export const CONTENT_FEED_ALL_SOCIALS = [
  "instagram",
  "youtube",
  "tiktok",
  "podcast",
  "twitter",
  "linkedin",
] as const;

export const CONTENT_FEED_ALL_FEED_TYPES = [
  "FEED",
  "REELS",
  "STORY",
  "HIGHLIGHTS",
  "PIN",
  "VIDEO",
  "PODCAST",
  "TIKTOK",
  "MAIL",
  "TWEET",
  "POST",
] as const;

export function getContentFeedPlatformLabel(
  platform: ContentFeedPlatform
): string {
  return (
    CONTENT_FEED_PLATFORMS.find((item) => item.id === platform)?.label ||
    platform
  );
}

export function isContentFeedPlatformConnected(
  platform: ContentFeedPlatform,
  integrationData: any
): boolean {
  if (!integrationData) return false;
  if (platform === "all") {
    return PLATFORM_PREFERENCE.some(
      (p) => p !== "all" && isContentFeedPlatformConnected(p, integrationData)
    );
  }
  switch (platform) {
    case "instagram":
      return Boolean(integrationData.instagram_username);
    case "youtube":
      return Boolean(integrationData.youtube_channel_id);
    case "tiktok":
      return Boolean(integrationData.tiktok_user);
    case "twitter":
      return Boolean(integrationData.twitter_user);
    case "linkedin":
      return Boolean(integrationData.linkedin_user);
    case "podcast":
      return Boolean(integrationData.podcast_link);
    default:
      return false;
  }
}

export function getDefaultContentFeedPlatform(
  integrationData: any
): ContentFeedPlatform {
  if (isContentFeedPlatformConnected("all", integrationData)) {
    return "all";
  }
  return (
    PLATFORM_PREFERENCE.find(
      (platform) =>
        platform !== "all" &&
        isContentFeedPlatformConnected(platform, integrationData)
    ) || "instagram"
  );
}

export function resolveContentFeedSelection(args: {
  platform: ContentFeedPlatform;
  instagramShow: InstagramShowOption;
  podcastDisplay: PodcastDisplayOption;
}): ContentFeedSelection {
  const { platform, instagramShow, podcastDisplay } = args;

  if (platform === "all") {
    return {
      kind: "carousel",
      contentType: "ALL",
      platform: "all",
      title: "Latest Content",
    };
  }

  if (platform === "instagram") {
    if (instagramShow === "stories") {
      return {
        kind: "carousel",
        contentType: "STORY",
        platform: "instagram",
        title: "Latest Instagram Stories",
      };
    }
    if (instagramShow === "all") {
      return {
        kind: "carousel",
        contentType: "REELS",
        platform: "instagram",
        title: "Latest Instagram",
        activeFilter: "all",
      };
    }
    const showLabel =
      INSTAGRAM_SHOW_OPTIONS.find((option) => option.id === instagramShow)
        ?.label || "Posts & Reels";
    return {
      kind: "carousel",
      contentType: "REELS",
      platform: "instagram",
      title: `Latest Instagram ${showLabel}`,
      activeFilter: instagramShow,
    };
  }

  if (platform === "tiktok") {
    return {
      kind: "carousel",
      contentType: "TIKTOK",
      platform: "tiktok",
      title: "Latest TikToks",
    };
  }

  if (platform === "youtube") {
    return {
      kind: "carousel",
      contentType: "VIDEO",
      platform: "youtube",
      title: "Latest YouTube Videos",
    };
  }

  if (platform === "twitter") {
    return {
      kind: "carousel",
      contentType: "TWEET",
      platform: "twitter",
      title: "Latest X Posts",
    };
  }

  if (platform === "linkedin") {
    return {
      kind: "carousel",
      contentType: "LINKEDIN",
      platform: "linkedin",
      title: "Latest LinkedIn Posts",
    };
  }

  if (podcastDisplay === "banner") {
    return {
      kind: "latest-podcast",
      contentType: "PODCAST",
      platform: "podcast",
      title: "Latest Podcast Episode",
    };
  }

  return {
    kind: "carousel",
    contentType: "PODCAST",
    platform: "podcast",
    title: "Latest Podcast Episodes",
  };
}

export type ContentFeedComponentSelection = {
  platform: ContentFeedPlatform;
  instagramShow: InstagramShowOption;
  podcastDisplay: PodcastDisplayOption;
};

function stayUpComponentsList(stayUp: any): any[] {
  if (!stayUp) return [];
  if (Array.isArray(stayUp)) return stayUp;
  const rows = [
    ...(stayUp?.data || []),
    ...(stayUp?.showingComponents || []),
    ...(stayUp?.archiveComponents || []),
  ];
  const seen = new Set<string>();
  return rows.filter((obj: any) => {
    const id = obj?.id != null ? String(obj.id) : "";
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function isStayUpComponentVisible(obj: any): boolean {
  const status = String(obj?.status_expiry ?? obj?.status ?? "")
    .toLowerCase()
    .trim();
  return status !== "hidden" && status !== "deleted";
}

export function isStayUpComponentPublished(obj: any): boolean {
  if (!obj || !isStayUpComponentVisible(obj)) return false;
  const status = String(obj?.status_expiry ?? obj?.status ?? "")
    .toLowerCase()
    .trim();
  return status === "active" || status === "";
}

function normalizeInstagramShow(filter: any): InstagramShowOption {
  const value = String(filter || "both").toLowerCase().trim();
  if (
    value === "all" ||
    value === "post" ||
    value === "reels" ||
    value === "both" ||
    value === "stories"
  ) {
    return value;
  }
  return "both";
}

export function parseContentFeedFromComponent(
  component: any
): ContentFeedComponentSelection | null {
  if (!component) return null;
  const dn = String(component?.display_name ?? "").toLowerCase().trim();
  const type = String(component?.type ?? "").toLowerCase().trim();

  if (dn === "all" || type === "all") {
    return {
      platform: "all",
      instagramShow: "both",
      podcastDisplay: "carousel",
    };
  }
  if (dn === "latest-podcast" || type === "latest-podcast") {
    return {
      platform: "podcast",
      instagramShow: "both",
      podcastDisplay: "banner",
    };
  }
  if (dn === "instagram story" || type === "story" || type === "instagram story") {
    return {
      platform: "instagram",
      instagramShow: "stories",
      podcastDisplay: "carousel",
    };
  }
  if (dn === "reels" || type === "reels") {
    return {
      platform: "instagram",
      instagramShow: normalizeInstagramShow(component?.shoppable_content_filter),
      podcastDisplay: "carousel",
    };
  }
  if (dn === "tiktok" || type === "tiktok") {
    return { platform: "tiktok", instagramShow: "both", podcastDisplay: "carousel" };
  }
  if (dn === "youtube" || type === "youtube") {
    return { platform: "youtube", instagramShow: "both", podcastDisplay: "carousel" };
  }
  if (dn === "twitter" || type === "twitter") {
    return { platform: "twitter", instagramShow: "both", podcastDisplay: "carousel" };
  }
  if (dn === "linkedin" || type === "linkedin") {
    return { platform: "linkedin", instagramShow: "both", podcastDisplay: "carousel" };
  }
  if (dn === "podcast" || type === "podcast") {
    return {
      platform: "podcast",
      instagramShow: "both",
      podcastDisplay: "carousel",
    };
  }
  return null;
}

export function contentFeedSelectionsEqual(
  a: ContentFeedComponentSelection | null,
  b: ContentFeedComponentSelection | null
): boolean {
  if (!a || !b) return false;
  if (a.platform !== b.platform) return false;
  if (a.platform === "instagram") return a.instagramShow === b.instagramShow;
  if (a.platform === "podcast") return a.podcastDisplay === b.podcastDisplay;
  return true;
}

export function findContentFeedComponent(
  stayUp: any,
  selection: ContentFeedComponentSelection,
  options?: { excludeId?: string | number | null }
): any | null {
  const excludeId = options?.excludeId != null ? String(options.excludeId) : null;
  return (
    stayUpComponentsList(stayUp).find((obj: any) => {
      if (!isStayUpComponentVisible(obj)) return false;
      if (excludeId && String(obj?.id) === excludeId) return false;
      return contentFeedSelectionsEqual(
        parseContentFeedFromComponent(obj),
        selection
      );
    }) || null
  );
}

export function isInstagramReelsShow(show: InstagramShowOption): boolean {
  return (
    show === "all" || show === "both" || show === "post" || show === "reels"
  );
}

export type ContentFeedStorageFields = {
  type: string;
  displayName: string;
  shoppableContentFilter: string | null;
};

export function getContentFeedStorageFields(
  selection: ContentFeedComponentSelection
): ContentFeedStorageFields {
  const resolved = resolveContentFeedSelection(selection);
  if (resolved.kind === "latest-podcast") {
    return {
      type: "latest-podcast",
      displayName: "latest-podcast",
      shoppableContentFilter: null,
    };
  }
  if (selection.platform === "all") {
    return { type: "all", displayName: "all", shoppableContentFilter: null };
  }
  if (selection.platform === "instagram" && selection.instagramShow === "stories") {
    return {
      type: "instagram story",
      displayName: "instagram story",
      shoppableContentFilter: null,
    };
  }
  if (selection.platform === "instagram") {
    return {
      type: "reels",
      displayName: "reels",
      shoppableContentFilter: selection.instagramShow,
    };
  }
  if (selection.platform === "tiktok") {
    return { type: "tiktok", displayName: "tiktok", shoppableContentFilter: null };
  }
  if (selection.platform === "youtube") {
    return { type: "youtube", displayName: "youtube", shoppableContentFilter: null };
  }
  if (selection.platform === "twitter") {
    return { type: "twitter", displayName: "twitter", shoppableContentFilter: null };
  }
  if (selection.platform === "linkedin") {
    return {
      type: "linkedin",
      displayName: "linkedin",
      shoppableContentFilter: null,
    };
  }
  return { type: "podcast", displayName: "podcast", shoppableContentFilter: null };
}

export function shouldClearPublishedContentFeedTitle(args: {
  component: any;
  opened: ContentFeedComponentSelection | null;
  next: ContentFeedComponentSelection;
}): boolean {
  if (!isStayUpComponentPublished(args.component)) return false;
  if (!args.opened) return false;
  return !contentFeedSelectionsEqual(args.opened, args.next);
}

/** Edit always stays on the opened row; platform/show changes convert that component. */
export function resolveContentFeedEditTarget(
  _stayUp: any,
  _selection: ContentFeedComponentSelection,
  opened: any
): any | null {
  return opened || null;
}

export function buildContentFeedEditorComponent(
  opened: any,
  selection: ContentFeedComponentSelection
): any {
  if (!opened) return opened;
  const openedParsed = parseContentFeedFromComponent(opened);
  const sameSelection = Boolean(
    openedParsed && contentFeedSelectionsEqual(openedParsed, selection)
  );
  if (sameSelection) {
    return opened;
  }

  const storage = getContentFeedStorageFields(selection);
  return {
    ...opened,
    type: storage.type,
    display_name: storage.displayName,
    shoppable_content_filter: storage.shoppableContentFilter || "both",
    // Platform / Show changes start a new feed — don't keep Instagram
    // "Shoppable" when switching to TikTok / YouTube / etc.
    shoppable_status_filter: "all",
    name: "",
    post_ids: "",
    removed_postids: "",
    custom_order: "",
    pinned_posts: null,
    query: "",
  };
}

export function getContentFeedEditParams(component: any): {
  type: string;
  platform: string;
  isPodcast: boolean;
} {
  const parsed = parseContentFeedFromComponent(component);
  const dn = String(component?.display_name ?? "").toLowerCase().trim();
  if (!parsed) {
    return { type: "REELS", platform: "instagram", isPodcast: false };
  }
  if (parsed.platform === "all") {
    return { type: "ALL", platform: "all", isPodcast: false };
  }
  if (parsed.platform === "instagram") {
    return {
      type: parsed.instagramShow === "stories" ? "STORY" : "REELS",
      platform: "instagram",
      isPodcast: false,
    };
  }
  if (parsed.platform === "tiktok") {
    return { type: "TIKTOK", platform: "tiktok", isPodcast: false };
  }
  if (parsed.platform === "youtube") {
    return { type: "VIDEO", platform: "youtube", isPodcast: false };
  }
  if (parsed.platform === "twitter") {
    return { type: "TWEET", platform: "twitter", isPodcast: false };
  }
  if (parsed.platform === "linkedin") {
    return { type: "POST", platform: "linkedin", isPodcast: false };
  }
  return {
    type: "PODCAST",
    platform: "podcast",
    isPodcast: dn === "podcast" || parsed.podcastDisplay === "carousel",
  };
}
